import * as React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { clsx } from 'clsx'

import { Button, Link, Text, VisuallyHidden } from '@buffer-mono/popcorn'

import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'
import { useTruncatedText } from '~publish/hooks/useTruncatedText'

import { usePostData } from './PostCardContext'
import { PostCardNotice } from './PostCardNotice'
import { usePostDetailsDialog } from '../PostDialogs/helpers'
import {
  shouldShowReconnectedMessage,
  RECONNECTED_CHANNEL_MESSAGE,
} from './helpers/authenticationErrorHelpers'

import styles from './PostCardErrorNotice.module.css'

import type { JSX } from 'react'

const CHANNEL_REFRESH_ARTICLE_ORIGIN = 'https://support.buffer.com'
const CHANNEL_REFRESH_ARTICLE_PATH =
  '/articles/refreshing-a-channel-in-buffer-7oDS4jk7l1'

const isChannelRefreshSupportUrl = (
  supportUrl: string | null | undefined,
): boolean => {
  if (!supportUrl) {
    return false
  }

  try {
    const url = new URL(supportUrl)
    return (
      url.origin === CHANNEL_REFRESH_ARTICLE_ORIGIN &&
      url.pathname === CHANNEL_REFRESH_ARTICLE_PATH
    )
  } catch {
    return false
  }
}

export const PostCardErrorNotice = ({
  clampErrorMessage = false,
  className,
  showRefreshConnectionAction: canShowRefreshConnectionAction = false,
}: {
  clampErrorMessage?: boolean
  className?: string
  showRefreshConnectionAction?: boolean
}): JSX.Element | null => {
  const { status, error, id, channel } = usePostData()
  const textRef = React.useRef<HTMLParagraphElement>(null)
  const refreshChannelConnection = useRefreshChannelConnection()
  const canManageChannels = useHasOrganizationAction('manageChannels')

  const { constructDetailsDialogLocation } = usePostDetailsDialog({
    postId: id,
  })

  const showReconnectedMessage = shouldShowReconnectedMessage(
    error?.message,
    channel.isDisconnected,
  )

  const displayMessage = showReconnectedMessage
    ? RECONNECTED_CHANNEL_MESSAGE
    : (error?.message ?? '')

  const [truncatedText, didTruncate] = useTruncatedText({
    text: displayMessage,
    lineClamp: 1,
    container: textRef,
    expansionText: '... more',
  })

  const showMoreLink = clampErrorMessage && didTruncate
  const isChannelRefreshError = isChannelRefreshSupportUrl(error?.supportUrl)
  const showRefreshConnectionAction =
    canShowRefreshConnectionAction &&
    !showReconnectedMessage &&
    (channel.isDisconnected || isChannelRefreshError)
  const canRefreshConnection = showRefreshConnectionAction && canManageChannels
  const showExternalHelpAction =
    error?.supportUrl && !showReconnectedMessage && !canRefreshConnection

  const detailsDialogLocation = React.useMemo(() => {
    return constructDetailsDialogLocation({
      cta: 'publish-calendar-postCardError-seeMore-1',
    })
  }, [constructDetailsDialogLocation])

  const hasErrorStatus = status === 'error' || status === 'sent'

  const handleRefreshConnection = (): void => {
    refreshChannelConnection({
      channel: {
        id: channel.id,
        service: channel.service,
        name: channel.name,
        apiVersion: channel.apiVersion,
      },
      cta: 'publish-allChannels-postCardError-refreshConnection-1',
    })
  }

  if (!error || !hasErrorStatus) {
    return null
  }

  return (
    <PostCardNotice variant="critical" className={className}>
      <VisuallyHidden>Error: </VisuallyHidden>
      <Text
        ref={textRef}
        className={clsx(
          styles.message,
          clampErrorMessage && !truncatedText && styles.clamped,
        )}
      >
        {clampErrorMessage && truncatedText ? truncatedText : displayMessage}
        {showMoreLink && (
          <>
            <span>...&nbsp;</span>
            <RouterLink to={detailsDialogLocation} className={styles.moreLink}>
              more
            </RouterLink>
          </>
        )}
        {canRefreshConnection && error.supportUrl && (
          <>
            {' '}
            <Link external href={error.supportUrl}>
              Learn more
            </Link>
          </>
        )}
      </Text>

      {canRefreshConnection && (
        <PostCardNotice.Action>
          <Button
            variant="critical"
            size="small"
            onClick={handleRefreshConnection}
          >
            Refresh Connection
          </Button>
        </PostCardNotice.Action>
      )}
      {showExternalHelpAction && (
        <PostCardNotice.Action>
          <Button
            variant="critical"
            size="small"
            as="a"
            href={error.supportUrl ?? undefined}
            rel="noopener noreferrer"
            target="_blank"
          >
            Learn More
          </Button>
        </PostCardNotice.Action>
      )}
    </PostCardNotice>
  )
}

PostCardErrorNotice.displayName = 'PostCardErrorNotice'
